import React from 'react';
import { Button, Backdrop, Tabs, Tab, Box, Typography } from '@material-ui/core';
import String from './String';
import Map from './Map';
import { MAPBOX_DARK_MAP_STYLE, MAPBOX_LIGHT_MAP_STYLE } from '../../config/map';
import { configHasGeoSpots, getMapSpotsFromConfig } from '../utils/geoXp';
import Layout from './Layout';
import Header from './Header';
import Footer from './Footer';
import { downloadAudio, downloadCredits, downloadVideo } from '../utils/offline';
import { sleep } from '../utils/utils';
import { useSession } from '../contexts/session';
import { useTheme } from '../contexts/theme';
import { useLanguage } from '../contexts/language';
import { useFocusRestore } from '../hooks/useFocusRestore';

function DownloadMapTilesHiddenOverlay({ experience, loaded }) {
  const [lightLoaded, setLightLoaded] = React.useState(false);
  const [darkLoaded, setDarkLoaded] = React.useState(false);
  const { content } = useLanguage();

  const mapSpots = React.useMemo(
    () => (experience?.config ? getMapSpotsFromConfig(experience.config) : []),
    [experience]
  );

  React.useEffect(() => {
    if (lightLoaded && darkLoaded && loaded) {
      console.info('Map tiles cached successfully');
      loaded();
    }
  }, [lightLoaded, darkLoaded, loaded]);

  return (
    <Backdrop style={{ position: 'fixed', opacity: 0, pointerEvents: 'none' }} open aria-hidden="true">
      <Layout
        fullscreen
        top={<Header />}
        bottom={
          <Footer>
            <Typography variant="caption" color="textSecondary">
              {content('footer_experience_prefix')}
              <a
                href="https://mezzoforte.design/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit' }}
              >
                {content('footer_experience_link')}
              </a>
            </Typography>
          </Footer>
        }
      >
        <Tabs value="map" style={{ width: '100%', display: 'flex' }}>
          <Tab label="Map" value="map" style={{ flex: 1 }} />
        </Tabs>
        <Layout>
          <Box width="100%" height="100%" position="relative">
            <Box width="100%" height="100%" position="absolute">
              <Map
                spots={mapSpots}
                styleOverride={MAPBOX_LIGHT_MAP_STYLE}
                loaded={() => setLightLoaded(true)}
              />
            </Box>
            <Box width="100%" height="100%" position="absolute">
              <Map
                spots={mapSpots}
                styleOverride={MAPBOX_DARK_MAP_STYLE}
                loaded={() => setDarkLoaded(true)}
              />
            </Box>
          </Box>
        </Layout>
      </Layout>
    </Backdrop>
  );
}

export function DownloadExperience({ experience, downloadComplete, downloadError, downloadCanceled }) {
  const { styles } = useTheme();
  const { saveExperience } = useSession();
  const { content } = useLanguage();

  const cancelButtonRef = React.useRef(null);

  const downloadAnnouncement = React.useMemo(() => {
    const template =
      content('aria_download_experience_in_progress') ??
      `${content('aria_download_experience') ?? 'Downloading experience'} {experience}`;
    return template.replace('{experience}', experience?.label ?? '').trim();
  }, [content, experience?.label]);

  useFocusRestore({ isOpen: true, focusRef: cancelButtonRef });

  const [downloadMapTiles, setDownloadMapTiles] = React.useState(null);
  const [progress, setProgress] = React.useState('preparing');

  const download = React.useCallback(
    async (abortSignal) => {
      await sleep(500);
      if (abortSignal.aborted) {
        downloadError(experience);
        return;
      }

      let saved;
      try {
        setProgress('downloading-config');
        saved = await saveExperience(experience, false, { silent: true });
      } catch {
        downloadError(experience);
        return;
      }

      if (!saved?.config || saved?.downloadError) {
        downloadError(saved ?? experience);
        return;
      }

      await sleep(500);
      if (abortSignal.aborted) {
        downloadError(saved);
        return;
      }

      if (saved.config?.audio?.sounds?.length) {
        setProgress('downloading-audio');
        const res = await downloadAudio(saved);
        if (abortSignal.aborted || res?.error) {
          downloadError(saved);
          return;
        }

        await sleep(500);
        if (abortSignal.aborted) {
          downloadError(saved);
          return;
        }
      }

      if (saved.config?.video?.videos?.length) {
        setProgress('downloading-video');
        const res = await downloadVideo(saved);
        if (abortSignal.aborted || res?.error) {
          downloadError(saved);
          return;
        }

        await sleep(500);
        if (abortSignal.aborted) {
          downloadError(saved);
          return;
        }
      }

      if (saved.creditsEnabled && saved.creditsBlocks?.length >= 0) {
        setProgress('downloading-credits');
        const res = await downloadCredits(saved);
        if (abortSignal.aborted || res?.error) {
          downloadError(saved);
          return;
        }

        await sleep(500);
        if (abortSignal.aborted) {
          downloadError(saved);
          return;
        }
      }

      if (configHasGeoSpots(saved.config)) {
        setProgress('downloading-maps');
        setDownloadMapTiles(saved);
      } else downloadComplete(saved);
    },
    [experience]
  );

  const mapLoaded = React.useCallback(async () => {
    await sleep(500);
    downloadComplete(downloadMapTiles);
  }, [downloadMapTiles]);

  const getProgress = React.useCallback(
    (current) => {
      switch (current) {
        case 'preparing':
          return content('download_experience_preparing');
        case 'downloading-config':
          return content('download_experience_config');
        case 'downloading-audio':
          return content('download_experience_audio');
        case 'downloading-video':
          return content('download_experience_video');
        case 'downloading-credits':
          return content('download_experience_credits');
        case 'downloading-maps':
          return content('download_experience_maps');
        default:
          return null;
      }
    },
    [content]
  );

  React.useEffect(() => {
    const abortController = new AbortController();
    download(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <>
      <Backdrop
        style={{ position: 'fixed', zIndex: 10, backgroundColor: '#000000aa' }}
        role="presentation"
        open
      >
        <Box
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          textAlign="center"
          p={4}
          style={{
            backgroundColor: styles.palette.background.default,
            borderRadius: '14px',
            width: '80%',
          }}
        >
          <Typography variant="title" mb={2}>
            <String s="download_experience_title" />
          </Typography>
          <Typography variant="text" mb={3}>
            {getProgress(progress)}
          </Typography>
          <Typography id="download-overlay-announcement" className="sr-only" aria-live="assertive">
            {downloadAnnouncement}
          </Typography>
          <Button
            ref={cancelButtonRef}
            color="secondary"
            variant="text"
            onClick={downloadCanceled}
            aria-label={content('aria_cancel_download')}
            aria-describedby="download-overlay-announcement"
          >
            <String s="cancel" />
          </Button>
        </Box>
      </Backdrop>
      {!!downloadMapTiles && (
        <DownloadMapTilesHiddenOverlay experience={downloadMapTiles} loaded={mapLoaded} />
      )}
    </>
  );
}
